/**
 * Authentication store
 * Uses Supabase-style server-side session management with Firebase Auth
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { onAuthStateChanged } from '../firebase/index.js';
/**
 * Auth store with persistence
 *
 * Note: Firebase Auth handles session persistence automatically via localStorage/IndexedDB.
 * The onAuthStateChanged listener will fire with the current user if a session exists,
 * even after page refresh. We don't persist the Firebase User object in Zustand because
 * it's not serializable - Firebase handles that separately.
 */
export const useAuthStore = create()(persist((set, get) => ({
    user: null,
    profile: null,
    isLoading: false, // Start as false - will be set to true during initialization if needed
    isAuthenticated: false,
    _unsubscribe: null, // Internal: stores unsubscribe function
    setUser: (user) => {
        set({
            user,
            isAuthenticated: !!user,
            isLoading: false, // Ensure loading is false when user is set
        });
    },
    setProfile: (profile) => {
        set({ profile });
    },
    setLoading: (isLoading) => {
        set({ isLoading });
    },
    initialize: async (sessionApi) => {
        // Don't set loading to true immediately - let pages render first
        // Only set to true if we actually need to wait for something
        // This prevents blocking public pages
        try {
            // FIXED: Use async version to wait for proper Firebase initialization
            const { getFirebaseAuthAsync } = await import('../firebase/auth');
            const auth = await getFirebaseAuthAsync();
            // Clean up existing listener if any
            const existingUnsubscribe = get()._unsubscribe;
            if (existingUnsubscribe) {
                existingUnsubscribe();
            }
            // Set up listener for Firebase Auth changes first (non-blocking)
            // This ensures we catch auth state changes immediately
            const unsubscribe = onAuthStateChanged((user) => {
                get().setUser(user);
                get().setLoading(false);
            });
            set({ _unsubscribe: unsubscribe });
            // Check Firebase Auth first (fast, non-blocking)
            const currentUser = auth.currentUser;
            if (currentUser) {
                // User is authenticated via Firebase Auth
                // Set loading to false immediately - user is authenticated
                get().setUser(currentUser);
                get().setLoading(false);
                // Try session API in background (non-blocking)
                if (sessionApi) {
                    sessionApi.getSession().then((sessionResponse) => {
                        if (sessionResponse.success && sessionResponse.data) {
                            // Session API succeeded, but we already have Firebase Auth
                            // No need to update state - Firebase Auth is sufficient
                            console.log('Session API check completed successfully');
                        }
                    }).catch((error) => {
                        // Session API failed, but we have Firebase Auth, so it's fine
                        console.warn('Session API check failed, using Firebase Auth:', error);
                    });
                }
                return;
            }
            // No Firebase Auth user - check session API if provided
            // But don't block - set loading to false immediately
            // Session API is optional - Firebase Auth is primary
            set({ isLoading: false });
            if (sessionApi) {
                // Try session API in background (non-blocking, don't await)
                // If it succeeds, update state. If it fails, that's fine - user is not authenticated
                sessionApi.getSession()
                    .then((sessionResponse) => {
                    if (sessionResponse.success && sessionResponse.data) {
                        // User is authenticated via session cookie
                        const authUser = sessionResponse.data;
                        // Set user state (we'll use a minimal User-like object)
                        set({
                            user: {
                                uid: authUser.uid,
                                email: authUser.email || null,
                                emailVerified: authUser.emailVerified || false,
                                displayName: authUser.displayName || null,
                                photoURL: authUser.photoURL || null,
                            },
                            isAuthenticated: true,
                            isLoading: false,
                        });
                    }
                })
                    .catch((error) => {
                    // Session check failed - that's fine, user is not authenticated
                    // Don't log as error - this is expected for unauthenticated users
                    console.debug('Session check failed (user not authenticated):', error);
                });
            }
        }
        catch (error) {
            // If initialization fails, set loading to false so pages can still render
            console.error('Auth initialization error:', error);
            set({ isLoading: false });
        }
    },
    cleanup: () => {
        const unsubscribe = get()._unsubscribe;
        if (unsubscribe) {
            unsubscribe();
            set({ _unsubscribe: null });
        }
    },
    signOut: async (sessionApi) => {
        // FIXED: Use async version to ensure Firebase is initialized
        const { getFirebaseAuthAsync } = await import('../firebase/auth');
        const auth = await getFirebaseAuthAsync();
        // Clear session cookie if session API is provided
        if (sessionApi) {
            try {
                await sessionApi.deleteSession();
            }
            catch (error) {
                console.warn('Failed to delete session:', error);
            }
        }
        // Sign out from Firebase Auth
        await auth.signOut();
        set({
            user: null,
            profile: null,
            isAuthenticated: false,
        });
    },
}), {
    name: 'voxapilot-auth',
    partialize: (state) => ({
        // Only persist profile, not the Firebase User object
        // Firebase Auth handles user session persistence separately
        // Exclude _unsubscribe (internal cleanup function)
        profile: state.profile,
    }),
}));
//# sourceMappingURL=auth.js.map